'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Globe, ExternalLink } from 'lucide-react';
import { useAuthContext } from '@/components/AuthProvider'; 
import { getSocials } from '@/components/config/socials';

import { useLanguage } from '@/components/LanguageProvider'; 
import { languagesConfig, Locale } from '@/i18n/dictionaries';

import { 
  IconTelegram, IconDiscord, IconYouTube, IconTikTok, IconGithub, 
  IconVisa, IconMastercard, IconMir, IconPaypal, IconX 
} from './SocialIcons';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  
  const { locale, setLocale, t } = useLanguage();
  const tf = t.footer; 

  const { regionData } = useAuthContext();
  const currency = regionData?.currency?.toUpperCase() || 'USD';
  const countryCode = regionData?.countryCode?.toUpperCase(); 
  const activeSocials = getSocials(locale).filter(s => s.showInFooter);

  const isInternational = ['EUR', 'USD', 'UAH'].includes(currency);
  const showLegalInfo = countryCode === 'RU';

  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- ЛОГИКА ОТОБРАЖЕНИЯ РЕЙТИНГА ---
  const cisCountries = ['RU', 'BY', 'KZ', 'AM', 'AZ', 'KG', 'MD', 'TJ', 'UZ', 'TM'];
  const uskCountries = ['DE']; 
  const ceroCountries = ['JP']; 
  const cadpaCountries = ['CN']; 
  const gsrrCountries = ['TW', 'HK']; 
  const pegiCountries = [
    'AL', 'AD', 'AT', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'GE', 'GR', 
    'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 
    'PT', 'RO', 'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'TR', 'GB', 'UA', 'VA',
    'DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CV', 'CM', 'CF', 'TD', 'KM', 'CG', 'CD', 'DJ', 'EG', 
    'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'CI', 'KE', 'LS', 'LR', 'LY', 'MG', 
    'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 
    'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW'
  ];

  let ratingConfig = null;

  if (countryCode) {
    if (cisCountries.includes(countryCode)) {
      ratingConfig = { src: '/ratings/rars.png', alt: '12+', width: 76 };
    } else if (uskCountries.includes(countryCode)) {
      ratingConfig = { src: '/ratings/usk.png', alt: 'USK 12', width: 212 };
    } else if (ceroCountries.includes(countryCode)) {
      ratingConfig = { src: '/ratings/CERO_B.svg', alt: 'CERO B', width: 76 };
    } else if (cadpaCountries.includes(countryCode)) {
      ratingConfig = { src: '/ratings/CADPA_12+.svg', alt: 'CADPA 12+', width: 76 };
    } else if (gsrrCountries.includes(countryCode)) {
      ratingConfig = { src: '/ratings/GSRR_PG_12_logo.webp', alt: 'GSRR 12+', width: 76 };
    } else if (pegiCountries.includes(countryCode)) {
      ratingConfig = { src: '/ratings/pegi.png', alt: 'PEGI 12', width: 212 };
    } else {
      ratingConfig = { src: '/ratings/esrb.png', alt: 'ESRB Teen', width: 212 };
    }
  }

  return (
    <footer className="w-full flex flex-col items-center pb-8 pt-12 relative">
      
      <div className="w-full max-w-[1400px] px-4 md:px-8 relative z-10">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {/* Левая колонка: Логотип, Описание, Соцсети */}
          <div className="lg:col-span-4 flex flex-col justify-between space-y-8">
            <div>
              <Link href="/" className="flex items-center gap-2 group w-fit">
                <Image
                  src="/logo.png"
                  alt="Royolands"
                  width={140}
                  height={32}
                  className="group-hover:opacity-80 transition-opacity duration-300 object-contain w-[140px]"
                  unoptimized
                />
              </Link>
              
              <div className="mt-6 space-y-1.5 text-[13px] text-white/40 font-medium leading-relaxed max-w-[300px]">
                <p className="text-white/60 mb-3">{tf.networkDesc}</p>
                {showLegalInfo && (
                  <>
                    <p>{tf.legalName}</p>
                    <p>{tf.legalOgrnip}</p>
                    <p>{tf.legalInn}</p>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2.5">
              {activeSocials.map((social) => {
                const Icon = social.icon;
                return (
                  <SocialBtn 
                    key={social.id}
                    icon={<Icon className={social.footerIconClass} />} 
                    href={social.href} 
                    label={social.label} 
                    hoverClass={social.footerHover} 
                  />
                );
              })}
            </div>
          </div> 

          {/* Центральная колонка: Навигация и Возрастной рейтинг */}
          <div className="lg:col-span-5 flex flex-col justify-between lg:px-8 space-y-8">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-[15px] text-white mb-5 tracking-wide">{tf.projectHeading}</h4>
                <ul className="space-y-3.5 text-[14px] font-medium text-white/60">
                  <li><FooterLink href="/news">{tf.navNews}</FooterLink></li>
                  <li><FooterLink href="/stats">{tf.navStats}</FooterLink></li>
                  <li><FooterLink href="https://rpwiki.royolands.com/">{tf.navWiki}</FooterLink></li>
                  <li><FooterLink href="/map">{tf.navMap}</FooterLink></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-[15px] text-white mb-5 tracking-wide">{tf.helpHeading}</h4>
                <ul className="space-y-3.5 text-[14px] font-medium text-white/60">
                  <li><FooterLink href="https://dsc.gg/rplands">{tf.navSupport}</FooterLink></li>
                  <li><FooterLink href="https://rpwiki.royolands.com/pravila/pravila-servera-rplands">{tf.navRules}</FooterLink></li>
                  <li><FooterLink href="/shop">{tf.navShop}</FooterLink></li>
                </ul>
              </div>
            </div>

            {/* Возрастной рейтинг с резервацией места (min-h) */}
            <div className="pt-4 flex justify-start w-full min-h-[80px] md:min-h-[96px]">
              {ratingConfig && (
                <div className="w-fit opacity-60 hover:opacity-100 transition-opacity duration-300 animate-in fade-in duration-500">
                  <Image 
                    src={ratingConfig.src} 
                    alt={ratingConfig.alt} 
                    width={ratingConfig.width} 
                    height={80} 
                    className="h-16 md:h-20 w-auto object-contain drop-shadow-lg" 
                    unoptimized 
                  />
                </div>
              )}
            </div>
          </div>

          {/* Правая колонка: Язык, Копирайт, Документы */}
          <div className="lg:col-span-3 flex flex-col justify-between items-start lg:items-end space-y-8 lg:space-y-0 relative">
            
            <div className="relative flex justify-start lg:justify-end w-full" ref={dropdownRef}>
              
              {/* Раздвигающаяся кнопка */}
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`relative flex items-center h-[44px] rounded-[22px] bg-transparent border-[2px] border-white/10 cursor-pointer overflow-hidden group transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] select-none ${
                  isLangMenuOpen 
                    ? 'max-w-[250px] bg-white/10' 
                    : 'max-w-[44px] hover:max-w-[250px] hover:bg-white/10'
                }`}
              >
                {/* Иконка Глобуса */}
                <div className="absolute -left-[2px] -top-[2px] w-[44px] h-[44px] flex items-center justify-center shrink-0">
                  <Globe size={18} className="text-white/70 group-hover:text-white transition-colors duration-300" />
                </div>

                {/* Выезжающий текст и стрелочка */}
                <div className="flex items-center text-white text-[14px] font-bold leading-none pl-[44px] pr-[16px] whitespace-nowrap h-full pt-[2px]">
                  <span className="shrink-0">{languagesConfig[locale].name}</span>
                  <i 
                    className={`block ml-2 w-0 h-0 border-t-[6px] border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent transition-transform duration-300 ${
                      isLangMenuOpen ? 'rotate-180 border-t-white' : 'border-t-[#999] group-hover:border-t-white'
                    }`} 
                  />
                </div>
              </button>

              {/* Выпадающее меню вниз */}
              <div 
                className={`absolute top-[calc(100%+12px)] right-auto lg:right-0 lg:left-auto w-[212px] bg-[#1c1c1c] rounded-[6px] p-2 flex flex-col z-[999] origin-top transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                  isLangMenuOpen 
                    ? 'opacity-100 visible translate-y-0 scale-100' 
                    : 'opacity-0 invisible -translate-y-2 scale-[0.96]'
                }`}
              >
                <div className="hidden lg:block absolute -top-[8px] right-[15px] w-0 h-0 border-b-[9px] border-b-[#1c1c1c] border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent" />
                <div className="block lg:hidden absolute -top-[8px] left-[15px] w-0 h-0 border-b-[9px] border-b-[#1c1c1c] border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent" />

                <div className="max-h-[240px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-[#555] [&::-webkit-scrollbar-thumb]:rounded-[4px]">
                  {Object.values(languagesConfig).map((lang) => (
                    <div
                      key={lang.code}
                      onClick={() => {
                        setLocale(lang.code as Locale);
                        setIsLangMenuOpen(false);
                      }}
                      className={`relative px-3 py-2 pl-7 text-[14px] leading-[1.5] cursor-pointer rounded-[4px] transition-colors duration-200 ${
                        locale === lang.code 
                          ? 'text-white' 
                          : 'text-[#919191] hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full bg-white ${locale === lang.code ? 'block' : 'hidden'}`} />
                      {lang.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-left lg:text-right w-full">
              <p className="text-[13px] text-white/40 font-medium leading-relaxed mb-4">
                &copy; {currentYear} ROYOLANDS LLC<br />
                {tf.notAffiliated}<br />
                {tf.mojangCurrency || "Внутриигровая валюта и виртуальные товары не являются официальным продуктом Minecraft, не одобрены и не связаны с Mojang AB."}
              </p>
              
              {/* === ИСПРАВЛЕННЫЙ БЛОК === */}
              {/* gap-4 lg:gap-5: дает стабильные отступы по бокам и снизу, flex-wrap переносит текст на новую строку если не влезает */}
              <div className="flex flex-wrap gap-3 md:gap-5 justify-start lg:justify-end text-[13px] font-semibold text-white/50 mt-1">
                <Link href="/legal/privacy" className="hover:text-white transition-colors duration-200">
                  {tf.privacyPolicy}
                </Link>
                <Link href="/legal/terms" className="hover:text-white transition-colors duration-200">
                  {tf.termsOfService}
                </Link>
              </div>
              
            </div>
          </div>
        </div>

        {/* Нижний бар с платежными системами */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <span className="text-[12px] font-bold text-white/30 uppercase tracking-wider">
              {tf.paymentText}
            </span>
            <div className="flex items-center gap-4 text-white/40">
              {isInternational ? (
                <>
                  <IconMastercard />
                  <IconVisa />
                  <IconPaypal />
                </>
              ) : (
                <>
                  <IconMir />
                  <IconVisa />
                  <IconMastercard />
                </>
              )}
            </div>
          </div>
          <p className="text-[13px] font-medium text-white/30 tracking-wide">
            {tf.teamProject}
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  const isExternal = href.startsWith('http');

  if (isExternal) {
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="group inline-flex items-center gap-1.5 hover:text-white transition-colors duration-200"
      >
        {children}
        <ExternalLink size={13} className="opacity-50 group-hover:opacity-100 transition-opacity" />
      </a>
    );
  }

  return (
    <Link href={href} className="inline-block hover:text-white transition-colors duration-200">
      {children}
    </Link>
  );
}

function SocialBtn({ icon, href, label, hoverClass }: { icon: React.ReactNode; href: string; label: string; hoverClass: string }) {
  return (
    <div className="relative w-11 h-11 shrink-0">
      <a 
        href={href}
        aria-label={label}
        target="_blank"
        rel="noopener noreferrer"
        className={`group absolute top-0 left-0 flex items-center h-11 rounded-full bg-white/[0.03] border border-white/5 text-white/60 transition-all duration-300 ease-out z-10 hover:z-20 overflow-hidden max-w-[44px] hover:max-w-[160px] active:scale-95 origin-left ${hoverClass}`}
      >
        <div className="w-11 h-11 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <span className="text-[13px] font-bold tracking-wider whitespace-nowrap opacity-0 transition-opacity duration-300 group-hover:opacity-100 pr-5">
          {label}
        </span>
      </a>
    </div>
  );
}